<template>
  <!-- Horizontal version -->
  <Link
    :field="link"
    v-if="cardLayout === MiniCardLayout.HORIZONTAL"
    class="group text-racq-med-grey no-underline active:no-underline"
  >
    <div
      class="flex self-center border-[1.5px] border-solid rounded-md py-3 pr-3 pl-4 min-h-[96px] h-full shadow-md hover:shadow-xl group-active:shadow-none"
      :class="greyBg?.value ? 'bg-racq-anti-flash-white' : 'bg-racq-true-white'"
    >
      <div class="flex flex-row flex-1">
        <div v-if="hasIcon" class="self-center mr-4 min-w-4 max-w-12">
          <Image :media="icon" />
        </div>
        <div class="flex flex-col self-center flex-1">
          <div class="text-input-label line-clamp-2">
            <RichText :field="title" class="group-active:text-racq-navy group-active:underline" />
          </div>
          <div v-if="description" class="text-block text-m-brand line-clamp-3">
            <RichText :field="description" class="group-active:text-racq-med-grey group-active:no-underline" />
          </div>
        </div>
        <div v-if="showChevron" class="flex self-center justify-center ml-3">
          <BaseIcon iconClass="bg-icon-chevron" class="!w-4 !h-4" />
        </div>
      </div>
    </div>
  </Link>
  <!-- Vertical Stacked version -->
  <Link
    :field="link"
    v-if="cardLayout === MiniCardLayout.VERTICAL_STACKED"
    class="group text-racq-med-grey no-underline active:no-underline"
  >
    <div
      class="flex self-center border-[1.5px] border-solid rounded-md pt-3 pr-3 pb-4 pl-4 min-h-[96px] h-full shadow-md hover:shadow-xl group-active:shadow-none"
      :class="greyBg?.value ? 'bg-racq-anti-flash-white' : 'bg-racq-true-white'"
    >
      <div class="flex flex-col w-full">
        <div v-if="hasIcon" class="self-start mb-[6px] min-w-4 max-w-12">
          <Image :media="icon" />
        </div>
        <div class="flex flex-row self-start items-baseline w-full">
          <div class="flex-1 text-input-label line-clamp-2">
            <RichText :field="title" class="group-active:text-racq-navy group-active:underline" />
          </div>
          <div v-if="showChevron" class="flex justify-center ml-auto pl-3">
            <BaseIcon iconClass="bg-icon-chevron" class="!w-4 !h-4" />
          </div>
        </div>
        <div v-if="description" class="text-block text-m-brand line-clamp-3 mr-7">
          <RichText :field="description" class="group-active:text-racq-med-grey group-active:no-underline" />
        </div>
      </div>
    </div>
  </Link>
  <!-- Horizontal with button version -->
  <div
    v-if="!showChevron && cardLayout === MiniCardLayout.HORIZONTAL_WITH_BUTTON"
    :class="[
      'flex flex-col md:flex-row border-[1.5px] border-solid rounded-md pt-5 pb-4 px-4 md:p-6 w-auto md:racq-min-w-lg  min-h-[96px] shadow-md',
      greyBg?.value ? 'bg-racq-anti-flash-white' : 'bg-racq-true-white',
      !fullWidth?.value && 'racq-max-w-xl5',
    ]"
  >
    <div class="flex flex-row flex-1">
      <div v-if="hasIcon" class="self-center mr-4 min-w-4 max-w-12">
        <Image :media="icon" />
      </div>
      <div class="flex flex-col self-center">
        <div class="text-input-label line-clamp-2">
          <RichText :field="title" />
        </div>
        <div v-if="description?.value" class="text-block text-m-brand line-clamp-3">
          <RichText :field="description" />
        </div>
      </div>
    </div>
    <SafeLink
      :field="link"
      class="self-center w-full md:w-max mt-5 md:mt-0 md:ml-3 active:no-underline"
      :class="`btn btn-md md:btn-sm ${buttonClass}`"
    />
  </div>
</template>

<script setup lang="ts">
import { RichText, Image, Link } from '@sitecore-jss/sitecore-jss-vue';
import { computed } from 'vue';
import BaseIcon from '@/components/BaseIcon/BaseIcon.vue';
import { IPromoCardMini } from '@/interfaces/entities/promo-card-mini.interface';
import { MiniCardLayout } from '@/enums/card-layout.enum';
import { ButtonStyle } from '@/enums/button-style.enum';
import { mapFieldToEnum } from '@/mappers/sc-field-to-enum.mapper';
import SafeLink from '@/components/SafeLink/SafeLink.vue';

// ----------------------------------------------------------------------------------
// Define variables
// ----------------------------------------------------------------------------------
const props = defineProps<Partial<IPromoCardMini>>();

const hasIcon = computed(() => !!props.icon?.value?.src);
const showChevron = computed(() => props.showChevron?.value);
const buttonStyle = computed<ButtonStyle | undefined>(() => mapFieldToEnum(ButtonStyle, props.buttonStyle));

const buttonClass = computed(() => {
  switch (buttonStyle.value) {
    case ButtonStyle.PRIMARY:
      return ['btn-primary'];
    case ButtonStyle.TERTIARY:
      return ['btn-tertiary'];
    default:
      return ['btn-secondary']; // fallback if null, empty, or anything else
  }
});
</script>
